import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Upload, Trash2, Loader2, Copy, CheckCircle2, Film, ExternalLink } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_BASE = window.location.origin;

const PRESENTATION_SUBCATEGORIES = [
  { id: "atlas", title: "Atlas Series", category: "Access Control" },
  { id: "biocv", title: "Bio CVSecurity", category: "Access Control" },
  { id: "armatura", title: "Armatura One", category: "Access Control" },
  { id: "speedlanes", title: "Speedlane Series", category: "Pedestrian Entrance Control" },
  { id: "turnstiles", title: "Turnstiles", category: "Pedestrian Entrance Control" },
  { id: "gates", title: "Swing Gates", category: "Pedestrian Entrance Control" },
  { id: "fht-perimeter", title: "Full Height Turnstiles", category: "Pedestrian Entrance Control" },
  { id: "biometric-terminals", title: "Biometric Terminals", category: "Time & Attendance" },
  { id: "software", title: "Software Solutions", category: "Time & Attendance" },
  { id: "vm-terminals", title: "Visitor Terminals", category: "Visitor Management" },
  { id: "vm-software", title: "VM Software", category: "Visitor Management" },
  { id: "walkthrough", title: "Walk-Through Detectors", category: "Security Inspection" },
  { id: "xray", title: "X-Ray Inspection", category: "Security Inspection" },
  { id: "road-blocker", title: "Road Blocker", category: "Perimeter Protection" },
  { id: "rising-bollards", title: "Rising Bollards", category: "Perimeter Protection" },
  { id: "barriers", title: "Parking Barriers", category: "Perimeter Protection" },
  { id: "anpr", title: "ANPR Camera", category: "Perimeter Protection" },
  { id: "tyre-killer", title: "Tyre Killer", category: "Perimeter Protection" },
  { id: "uvis", title: "Under Vehicle Inspection System", category: "Perimeter Protection" },
  { id: "people-counting", title: "People Counting", category: "AI & Analytics" },
  { id: "facial-analytics", title: "Facial Analytics", category: "AI & Analytics" },
  { id: "behaviour", title: "Behaviour Detection", category: "AI & Analytics" },
];

export default function ResourceManager({ onBack }) {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  // Video assignments
  const [assignments, setAssignments] = useState({});

  // Datasheets
  const [datasheets, setDatasheets] = useState({});
  const [uploadingDs, setUploadingDs] = useState("");

  // Upload form
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [slug, setSlug] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const fileRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchResources();
    fetchCategories();
    fetchAssignments();
    fetchDatasheets();
  }, []);

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/resources/list`, { headers: getAuthHeaders() });
      setResources(res.data || []);
    } catch (err) {
      console.error("Failed to fetch resources:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/resources/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API}/resources/video-assignments`, { headers: getAuthHeaders() });
      setAssignments(res.data || {});
    } catch (err) {
      console.error("Failed to fetch assignments:", err);
    }
  };

  const assignVideo = async (subcategoryId, resourceId) => {
    try {
      await axios.post(`${API}/resources/assign-video`, { subcategory_id: subcategoryId, resource_id: resourceId }, { headers: getAuthHeaders() });
      setAssignments((prev) => {
        const next = { ...prev };
        if (resourceId) next[subcategoryId] = resourceId;
        else delete next[subcategoryId];
        return next;
      });
    } catch (err) {
      console.error("Failed to assign video:", err);
    }
  };

  const fetchDatasheets = async () => {
    try {
      const res = await axios.get(`${API}/presentation/datasheets`, { headers: getAuthHeaders() });
      setDatasheets(res.data || {});
    } catch (err) {
      console.error("Failed to fetch datasheets:", err);
    }
  };

  const uploadDatasheet = async (subcategoryId, file) => {
    if (!file) return;
    setUploadingDs(subcategoryId);
    const formData = new FormData();
    formData.append("subcategory_id", subcategoryId);
    formData.append("file", file);
    try {
      await axios.post(`${API}/presentation/upload-datasheet`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      await fetchDatasheets();
    } catch (err) {
      console.error("Failed to upload datasheet:", err);
    } finally {
      setUploadingDs("");
    }
  };

  const handleUpload = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    if (!category) { setError("Category is required."); return; }
    if (!selectedFile) { setError("Please select a video file."); return; }
    if (!slug.trim()) { setError("Link name is required."); return; }
    setError("");
    setUploading(true);
    setUploadProgress(0);

    const CHUNK_SIZE = 4 * 1024 * 1024; // 4MB chunks
    const totalChunks = Math.ceil(selectedFile.size / CHUNK_SIZE);
    const resourceId = slug.trim();

    try {
      // Upload chunks
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, selectedFile.size);
        const chunk = selectedFile.slice(start, end);

        const chunkForm = new FormData();
        chunkForm.append("chunk_index", i.toString());
        chunkForm.append("total_chunks", totalChunks.toString());
        chunkForm.append("resource_id", resourceId);
        chunkForm.append("file", chunk, `chunk_${i}`);

        await axios.post(`${API}/resources/upload-chunk`, chunkForm, {
          headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        });

        setUploadProgress(Math.round(((i + 1) / totalChunks) * 95));
      }

      // Complete upload
      await axios.post(`${API}/resources/upload-complete`, {
        resource_id: resourceId,
        title: title.trim(),
        category,
        description: description.trim(),
        original_filename: selectedFile.name,
        content_type: selectedFile.type || "video/mp4",
        total_chunks: totalChunks,
      }, { headers: getAuthHeaders() });

      setUploadProgress(100);
      setTitle("");
      setCategory("");
      setDescription("");
      setSlug("");
      setSelectedFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await fetchResources();
    } catch (err) {
      console.error("Upload failed:", err);
      setError(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await axios.delete(`${API}/resources/${id}`, { headers: getAuthHeaders() });
      setResources((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const copyLink = (id) => {
    const link = `https://www.zkuk-config.co.uk/resource/${id}`;
    navigator.clipboard.writeText(link);
    setCopied(id);
    setTimeout(() => setCopied(""), 2000);
  };

  const togglePublish = async (id, currentState) => {
    try {
      await axios.patch(`${API}/resources/${id}/publish`, { published: !currentState }, { headers: getAuthHeaders() });
      setResources((prev) => prev.map((r) => r.id === id ? { ...r, published: !currentState } : r));
    } catch (err) {
      console.error("Toggle publish failed:", err);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)}GB`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="resources-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="resources-title">Resource Manager</h1>
          <p className="text-sm text-slate-500 mt-1">Upload videos and get shareable links — no login required to view.</p>
        </div>

        {/* Upload Form */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
          <h2 className="text-base font-bold text-slate-800 mb-4">Upload New Resource</h2>
          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <div>
              <Label className="text-sm text-slate-600">Title *</Label>
              <Input value={title} onChange={(e) => {
                setTitle(e.target.value);
                if (!slug || slug === title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")) {
                  setSlug(e.target.value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""));
                }
              }} placeholder="Video title..." className="mt-1" data-testid="resource-title-input" />
            </div>
            <div>
              <Label className="text-sm text-slate-600">Category *</Label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full h-9 text-sm border border-slate-200 rounded-md px-3 bg-white" data-testid="resource-category-select">
                <option value="">Select category...</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-sm text-slate-600">Link Name *</Label>
            <div className="mt-1 flex items-center gap-0">
              <span className="text-xs text-slate-400 bg-slate-100 border border-r-0 border-slate-200 rounded-l-md px-2 h-9 flex items-center whitespace-nowrap">/resource/</span>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                placeholder="introduction-to-ultima"
                className="rounded-l-none"
                data-testid="resource-slug-input"
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">This will be your shareable link: zkuk-config.co.uk/resource/{slug || "..."}</p>
          </div>
          <div className="mb-4">
            <Label className="text-sm text-slate-600">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description..." className="mt-1 text-sm" rows={2} data-testid="resource-description" />
          </div>
          <div className="mb-4">
            <Label className="text-sm text-slate-600">Video File *</Label>
            <div className="mt-1 flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="resource-file-btn">
                <Upload className="h-3.5 w-3.5 mr-1" /> Choose File
              </Button>
              <span className="text-sm text-slate-500">{selectedFile ? `${selectedFile.name} (${formatSize(selectedFile.size)})` : "No file selected"}</span>
              <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
            </div>
          </div>

          {error && <p className="text-sm text-red-600 mb-3" data-testid="resource-error">{error}</p>}

          {uploading && (
            <div className="mb-3">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1">Uploading... {uploadProgress}%</p>
            </div>
          )}

          <Button onClick={handleUpload} disabled={uploading} className="bg-green-600 hover:bg-green-700" data-testid="resource-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? "Uploading..." : "Upload Resource"}
          </Button>
        </div>

        {/* Resource List */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
          <h2 className="text-base font-bold text-slate-800 mb-4">Uploaded Resources ({resources.length})</h2>
          {loading ? (
            <p className="text-slate-500">Loading...</p>
          ) : resources.length === 0 ? (
            <div className="text-center py-10">
              <Film className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No resources uploaded yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {resources.map((r) => (
                <div key={r.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3" data-testid={`resource-item-${r.id}`}>
                  <div className="flex items-center gap-3 flex-1 min-w-0 mr-3">
                    <label className="flex items-center gap-0 cursor-pointer flex-shrink-0" title={r.published ? "Published — visible to distributors/installers" : "Not published"}>
                      <input
                        type="checkbox"
                        checked={!!r.published}
                        onChange={() => togglePublish(r.id, r.published)}
                        className="h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                        data-testid={`publish-${r.id}`}
                      />
                    </label>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {r.title}
                        {r.published && <span className="ml-2 text-[10px] font-semibold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">LIVE</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded text-[10px] font-semibold mr-2">{r.category}</span>
                        {formatSize(r.file_size)} &middot; {r.created_at ? new Date(r.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" className="text-sm h-8" onClick={() => copyLink(r.id)} data-testid={`copy-link-${r.id}`}>
                      {copied === r.id ? <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-green-600" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
                      {copied === r.id ? "Copied!" : "Copy Link"}
                    </Button>
                    <a href={`/resource/${r.id}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="text-sm h-8">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200 h-8" onClick={() => handleDelete(r.id)} data-testid={`delete-resource-${r.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Presentation Video Assignments */}
        {resources.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mt-6" data-testid="video-assignments">
            <h2 className="text-base font-bold text-slate-800 mb-1">Presentation Video Assignments</h2>
            <p className="text-xs text-slate-500 mb-4">Assign uploaded videos to presentation subcategories.</p>
            <div className="space-y-3">
              {PRESENTATION_SUBCATEGORIES.map((sub) => (
                <div key={sub.id} className="bg-slate-50 rounded-lg px-4 py-3" data-testid={`assign-${sub.id}`}>
                  <p className="text-sm font-medium text-slate-700 mb-2">{sub.category} &rsaquo; {sub.title}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase">Video</label>
                      <select
                        value={assignments[sub.id] || ""}
                        onChange={(e) => assignVideo(sub.id, e.target.value)}
                        className="w-full h-8 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700"
                      >
                        <option value="">No video assigned</option>
                        {resources.map((r) => (
                          <option key={r.id} value={r.id}>{r.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-[10px] font-semibold text-slate-400 uppercase">Datasheet</label>
                      <div className="flex items-center gap-2">
                        {datasheets[sub.id] ? (
                          <span className="text-xs text-green-600 font-medium truncate flex-1">{datasheets[sub.id].filename}</span>
                        ) : (
                          <span className="text-xs text-slate-400 flex-1">No datasheet</span>
                        )}
                        <label className="cursor-pointer">
                          <span className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                            {uploadingDs === sub.id ? "Uploading..." : datasheets[sub.id] ? "Replace" : "Upload"}
                          </span>
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={(e) => uploadDatasheet(sub.id, e.target.files?.[0])}
                            disabled={uploadingDs === sub.id}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
